/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Body, Box, Heading, Link, Loader, Sheet } from '@hover/blueprint';
import React from 'react';
import { generatePath, useHistory } from 'react-router';

import { useLogout } from 'src/modules/logout/hooks/useLogout';
import { useFeatureAccesses } from 'src/modules/propertyDetails/hooks/useFeatureAccesses';
import { useHasAccessForOrg } from 'src/modules/propertyDetails/hooks/useHasAccessForOrg';
import { useIsHoverAdmin } from 'src/modules/propertyDetails/hooks/useIsHoverAdmin';
import { Image } from 'src/shared/components';
import { FeatureFlag } from 'src/shared/constants';
import {
  AclTemplateOptions,
  useProfileQuery,
} from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useTranslate,
  useTreatment,
} from 'src/shared/hooks';
import { useBrand } from 'src/shared/providers/Brand';
import { External, Route } from 'src/shared/route';
import { isFlagEnabled } from 'src/shared/telemetry';

export enum NavigationLinks {
  Projects = 'Projects',
  ManageTeam = 'ManageTeam',
  ProjectsList = 'ProjectsList', // sigh
}

interface NavMenuItemProps {
  path: string;
  display: string;
  textStyle?: string;
  marginBottom?: number;
}

const NavMenuItem: React.FC<NavMenuItemProps> = ({
  path,
  display,
  ...props
}) => (
  <Link
    color="neutral.900"
    fontWeight="bold"
    href={path}
    isExternal
    textDecoration="none"
    whiteSpace="nowrap"
    {...props}
  >
    {display}
  </Link>
);

const MobileNavSheet: React.FC<{
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
}> = ({ isOpen, setIsOpen }) => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const { data } = useProfileQuery();
  const { isEstimationEnabled } = useFeatureAccesses();
  const isAnalyticsPageEnabled = isFlagEnabled(
    useTreatment(FeatureFlag.Web.ANALYTICS_PAGE),
  );
  const [isTeamAdmin] = useHasAccessForOrg([AclTemplateOptions.Admin]);
  const [logout, { isPending: isLogoutPending }] = useLogout();
  const history = useHistory();

  const isHoverAdmin = useIsHoverAdmin();
  const isHomeowner = data?.profile?.user?.isHomeowner;
  const { isUpgradeToProEnabled } = useFeatureAccesses();

  const user = data?.profile?.user;
  const org = user?.orgs[0];

  const walletBalance = org?.wallet?.totalBalance
    ? org?.wallet?.totalBalance / 100.0
    : 0;

  const { defaultLogo, loadingBrand, coloredLogo } = useBrand();

  const logo = !coloredLogo || org?.id === 1 ? defaultLogo : coloredLogo;
  if (loadingBrand) return null;

  return (
    <Sheet
      header=""
      isOpen={isOpen}
      maxContentHeight={{
        base: '75vh',
      }}
      onClose={() => setIsOpen(false)}
    >
      <Box flexDirection="column" height="75vh" paddingBottom={300}>
        <Image
          alt={t('logoWithText', { name: 'HOVER' })}
          height={200}
          onClick={() => history.push(Route.PROPERTIES.path)}
          paddingBottom={400}
          paddingRight={{ base: 300, tablet: 200, mobile: 200 }}
          paddingTop={100}
          src={logo}
        />
        {isTeamAdmin && (
          <Box paddingY={300}>
            <Body color="neutral.500" marginY={0}>
              {t('currency', { val: walletBalance })}&nbsp;
              {t('navigation.credit')}
            </Body>
          </Box>
        )}
        <NavMenuItem
          display={t('navigation.Jobs')}
          marginBottom={400}
          path={Route.PROPERTIES.path}
          textStyle="heading600"
        />
        {isTeamAdmin && (
          <NavMenuItem
            display={t('navigation.manageTeam')}
            marginBottom={400}
            path={generatePath(Route.MANAGE_TEAMS.path, {
              orgId: org?.id as number,
            })}
            textStyle="heading600"
          />
        )}
        {isEstimationEnabled && (
          <NavMenuItem
            display={t('navigation.Estimates')}
            marginBottom={400}
            path={External.PROJECT_LIST.path}
            textStyle="heading600"
          />
        )}
        {isAnalyticsPageEnabled && isTeamAdmin && (
          <NavMenuItem
            display={t('navigation.analytics')}
            marginBottom={400}
            path={External.ANALYTICS.path}
            textStyle="heading600"
          />
        )}
        {isHoverAdmin && (
          <NavMenuItem
            display={t('navigation.admin')}
            marginBottom={300}
            path={External.ADMIN.path}
            textStyle="heading300"
          />
        )}
        {isTeamAdmin && (
          <NavMenuItem
            display={t('navigation.planAndBilling')}
            marginBottom={300}
            path={External.PLAN.path}
            textStyle="heading300"
          />
        )}
        {!isHomeowner && (
          <NavMenuItem
            display={t('navigation.settings')}
            marginBottom={300}
            path={External.SETTINGS.path}
            textStyle="heading300"
          />
        )}
        <Heading
          color="neutral.900"
          cursor="pointer"
          onClick={logout}
          size={300}
          whiteSpace="nowrap"
        >
          {t('logOut')}
          {isLogoutPending && <Loader ml="200" size="small" />}
        </Heading>
        {isHomeowner && isUpgradeToProEnabled && (
          <Box justifyContent="center" paddingY={300}>
            <Box
              backgroundColor="neutral.100"
              borderRadius="10px"
              flexDirection="row"
              justifyContent="space-between"
              paddingX="300"
              width="100%"
            >
              <Body fontWeight="bodyBold">{t('navigation.professional')}</Body>
              <Link
                color="neutral.900"
                href={`${Route.SIGN_UP.path}?pro=true&user_id=${
                  user?.id ?? ''
                }`}
                textDecoration="underline"
              >
                {t('navigation.upgrade')}
              </Link>
            </Box>
          </Box>
        )}
      </Box>
    </Sheet>
  );
};

export { MobileNavSheet };
